/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import {
  acceptOrganizationInvitationUrl,
  addRoleAssignmentsUrl,
  createOrganizationInvitationsUrl,
  createOrganizationMembershipUrl,
  createOrganizationUrl,
  deleteOrganizationInvitationsUrl,
  deleteOrganizationMembershipsUrl,
  getOrganizationConfigurationUrl,
  getOrganizationInvitationUrl,
  getOrganizationUrl,
  listOrganizationInvitationsUrl,
  listOrganizationMembersUrl,
  listOrganizationsUrl,
  patchOrganizationUrl,
  refreshSaasCurrentUserUrl,
  removeRoleAssignmentsUrl,
  updateOrganizationConfigurationUrl,
} from '@modules/cloud-api/v1/urls'
import { applyParameters } from '@modules/query/helpers'
import type {
  OrganizationConfigurationUpdateRequest,
  OrganizationInvitationRequest,
  OrganizationMembershipRequest,
  OrganizationRequest,
  RoleAssignments,
} from '@modules/cloud-api/v1/types'
import { isRoleAssignmentsEmpty } from '@modules/role-assignments-lib'
import {
  invalidateGetOrganizationQuery,
  invalidateGetSaasUserQuery,
  invalidateGetUserProfileQuery,
} from '@modules/profile-lib/hooks'
import { invalidateGetUserMembershipsQuery } from '@modules/organization-lib/hooks'

import { fetchProfile } from '@/apps/userconsole/actions/profile'
import {
  ACCEPT_ORGANIZATION_INVITATION,
  ADD_ORGANIZATION_MEMBER,
  ADD_ORGANIZATION_MEMBERSHIP_ROLE_ASSIGNMENTS,
  DELETE_ORGANIZATION_MEMBERSHIP_ROLE_ASSIGNMENTS,
  CREATE_ORGANIZATION,
  DELETE_ORGANIZATION_INVITATIONS,
  DELETE_ORGANIZATION_MEMBERSHIPS,
  FETCH_ORGANIZATION,
  FETCH_ORGANIZATION_INVITATION,
  FETCH_ORGANIZATION_INVITATIONS,
  FETCH_ORGANIZATION_MEMBERSHIPS,
  FETCH_ORGANIZATIONS,
  REFRESH_SAAS_CURRENT_USER,
  UPDATE_ORGANIZATION,
  UPSERT_ORGANIZATION_INVITATION,
  UPDATE_ORGANIZATION_MEMBERSHIP_ROLE_ASSIGNMENTS,
  UPDATE_ORGANIZATION_CONFIGURATION,
  FETCH_ORGANIZATION_CONFIGURATION,
} from '@/constants/actions'
import { fetchOrganizationRequest } from '@/reducers/asyncRequests/registry'
import { getOrganization } from '@/reducers/organizations'
import asyncRequest, { resetAsyncRequest } from '@/actions/asyncRequests'
import { type RoleAssignmentsDiff } from '@/components/Users/RoleAssignmentsPanel/lib'

import { fetchAuthzRoles } from '../authzRoles'

import type { AdjustedOrganizationRequest } from './types'
import type { ThunkAction } from '@/types/redux'

export function fetchOrganizations({ organizationName }: { organizationName: string }) {
  const url = listOrganizationsUrl({ q: `name:${organizationName}`, limit: 20 })

  return asyncRequest({
    type: FETCH_ORGANIZATIONS,
    method: `GET`,
    url,
  })
}

export function fetchOrganizationsByEntitlementId({ entitlementId }: { entitlementId: string }) {
  const url = applyParameters('/api/v2/billing/organizations', {
    queryParameters: { entitlement_id: entitlementId },
  })

  return asyncRequest({
    type: FETCH_ORGANIZATIONS,
    method: `GET`,
    url,
    meta: { entitlementId },
  })
}

export function fetchOrganization({ organizationId }: { organizationId: string }) {
  const url = getOrganizationUrl({ organizationId })

  return asyncRequest({
    type: FETCH_ORGANIZATION,
    method: `GET`,
    url,
    meta: { organizationId },
    crumbs: [organizationId],
  })
}

export function refreshSaasCurrentUser() {
  const url = refreshSaasCurrentUserUrl()

  return asyncRequest({
    type: REFRESH_SAAS_CURRENT_USER,
    method: `POST`,
    url,
  })
}

function shouldFetchOrganization(state, organizationId: string) {
  const organizationRequest = fetchOrganizationRequest(state, organizationId)

  if (organizationRequest.inProgress) {
    return false
  }

  return !getOrganization(state, organizationId)
}

export function fetchOrganizationIfNeeded({ organizationId }: { organizationId: string }) {
  return (dispatch, getState) => {
    if (shouldFetchOrganization(getState(), organizationId)) {
      return dispatch(fetchOrganization({ organizationId }))
    }

    return Promise.resolve()
  }
}

export function updateOrganization({
  organizationId,
  organization,
}: {
  organizationId: string
  organization: AdjustedOrganizationRequest | OrganizationRequest
}) {
  const url = patchOrganizationUrl({ organizationId })

  return (dispatch) =>
    dispatch(
      asyncRequest({
        type: UPDATE_ORGANIZATION,
        method: `PATCH`,
        url,
        meta: { organizationId },
        crumbs: [organizationId],
        payload: organization,
      }),
    )
      .then(() => invalidateGetOrganizationQuery())
      .then(() => invalidateGetUserMembershipsQuery())
}

export function createOrganization({ name }: { name?: string }): ThunkAction {
  const url = createOrganizationUrl()

  const payload: OrganizationRequest = {
    name,
  }

  return (dispatch) =>
    dispatch(
      asyncRequest({
        type: CREATE_ORGANIZATION,
        method: `POST`,
        url,
        payload,
      }),
    )
      .then(() => dispatch(refreshSaasCurrentUser()))
      .then(() => dispatch(fetchAuthzRoles()))
      .then(() => dispatch(fetchProfile({ showRoleAssignments: true })))
      .then(() => invalidateGetUserProfileQuery())
      .then(() => invalidateGetSaasUserQuery())
      .then(() => invalidateGetUserMembershipsQuery())
}

export function acceptOrganizationInvitation({
  invitationToken,
}: {
  invitationToken: string
}): ThunkAction {
  const url = acceptOrganizationInvitationUrl({ invitationToken })

  return (dispatch) =>
    dispatch(
      asyncRequest({
        type: ACCEPT_ORGANIZATION_INVITATION,
        method: `POST`,
        url,
        meta: { invitationToken },
        crumbs: [invitationToken],
      }),
    )
      .then(() => dispatch(refreshSaasCurrentUser()))
      .then(() => dispatch(fetchAuthzRoles()))
      .then(() => dispatch(fetchProfile({ showRoleAssignments: true })))
      .then(() => invalidateGetSaasUserQuery())
      .then(() => invalidateGetUserMembershipsQuery())
      .then(() => invalidateGetUserProfileQuery())
}

export function fetchOrganizationInvitation({ invitationToken }: { invitationToken: string }) {
  const url = getOrganizationInvitationUrl({ invitationToken })

  return asyncRequest({
    type: FETCH_ORGANIZATION_INVITATION,
    method: `GET`,
    url,
    meta: { invitationToken },
    crumbs: [invitationToken],
  })
}

export function fetchOrganizationInvitations({ organizationId }: { organizationId: string }) {
  const url = listOrganizationInvitationsUrl({ organizationId })

  return asyncRequest({
    type: FETCH_ORGANIZATION_INVITATIONS,
    method: `GET`,
    url,
    meta: { organizationId },
    crumbs: [organizationId],
  })
}

export function upsertOrganizationInvitations({
  organizationId,
  emails,
  roleAssignments,
}: {
  organizationId: string
  emails: string[]
  roleAssignments?: RoleAssignments
}) {
  const url = createOrganizationInvitationsUrl({ organizationId })

  const payload: OrganizationInvitationRequest = {
    emails,
    role_assignments: roleAssignments,
  }

  return asyncRequest({
    type: UPSERT_ORGANIZATION_INVITATION,
    method: `POST`,
    url,
    meta: { organizationId, emails, roleAssignments },
    crumbs: [organizationId, emails.join(',')],
    payload,
  })
}

export function deleteOrganizationInvitations({
  organizationId,
  tokens,
}: {
  organizationId: string
  tokens: string[]
}) {
  const concatenatedTokens = tokens.join(',')

  const url = deleteOrganizationInvitationsUrl({
    organizationId,
    invitationTokens: concatenatedTokens,
  })

  return asyncRequest({
    type: DELETE_ORGANIZATION_INVITATIONS,
    method: `DELETE`,
    url,
    meta: { organizationId, tokens },
    crumbs: [organizationId, concatenatedTokens],
  })
}

export function fetchOrganizationMemberships({ organizationId }: { organizationId: string }) {
  const url = listOrganizationMembersUrl({ organizationId })

  return asyncRequest({
    type: FETCH_ORGANIZATION_MEMBERSHIPS,
    method: `GET`,
    url,
    meta: { organizationId },
    crumbs: [organizationId],
  })
}

export function deleteOrganizationMemberships({
  organizationId,
  userIds,
}: {
  organizationId: string
  userIds: string[]
}) {
  const concatenatedUserIds = userIds.join(',')
  const url = deleteOrganizationMembershipsUrl({ organizationId, userIds: concatenatedUserIds })

  return asyncRequest({
    type: DELETE_ORGANIZATION_MEMBERSHIPS,
    method: `DELETE`,
    url,
    meta: { organizationId, userIds },
    crumbs: [organizationId, concatenatedUserIds],
  })
}

export function addOrganizationMember({
  userId,
  organizationId,
}: {
  userId: string
  organizationId: string
}): ThunkAction {
  const url = createOrganizationMembershipUrl({ organizationId })

  const payload: OrganizationMembershipRequest = {
    user_id: userId,
  }

  return (dispatch) =>
    dispatch(
      asyncRequest({
        type: ADD_ORGANIZATION_MEMBER,
        method: `POST`,
        url,
        payload,
      }),
    )
}

export function updateRoleAssignments({
  organizationId,
  userId,
  roleAssignments,
  roleAssignmentsDiff,
}: {
  organizationId: string
  userId: string
  roleAssignments: RoleAssignments
  roleAssignmentsDiff: RoleAssignmentsDiff
}): ThunkAction {
  const addUrl = addRoleAssignmentsUrl({ userId })
  const removeUrl = removeRoleAssignmentsUrl({ userId })

  const { added, removed } = roleAssignmentsDiff

  return (dispatch) =>
    Promise.resolve()
      .then(() => {
        if (!isRoleAssignmentsEmpty(removed)) {
          return dispatch(
            asyncRequest({
              type: DELETE_ORGANIZATION_MEMBERSHIP_ROLE_ASSIGNMENTS,
              method: `DELETE`,
              url: removeUrl,
              crumbs: [organizationId, userId],
              payload: removed,
            }),
          )
        }

        return Promise.resolve({})
      })
      .then(() => {
        if (!isRoleAssignmentsEmpty(added)) {
          return dispatch(
            asyncRequest({
              type: ADD_ORGANIZATION_MEMBERSHIP_ROLE_ASSIGNMENTS,
              method: `POST`,
              url: addUrl,
              crumbs: [organizationId, userId],
              payload: added,
            }),
          )
        }

        return Promise.resolve({})
      })
      .then(() =>
        dispatch({
          type: UPDATE_ORGANIZATION_MEMBERSHIP_ROLE_ASSIGNMENTS,
          meta: { organizationId, userId, roleAssignments },
        }),
      )
}

export function fetchOrganizationConfiguration({
  organizationId,
}: {
  organizationId: string
}): ThunkAction {
  const url = getOrganizationConfigurationUrl({ organizationId })

  return (dispatch) =>
    dispatch(
      asyncRequest({
        type: FETCH_ORGANIZATION_CONFIGURATION,
        method: `GET`,
        url,
        meta: { organizationId },
        crumbs: [organizationId],
      }),
    )
}

export function updateOrganizationConfiguration({
  organizationId,
  updateRequest,
}: {
  organizationId: string
  updateRequest: OrganizationConfigurationUpdateRequest
}): ThunkAction {
  const url = updateOrganizationConfigurationUrl({ organizationId })

  return (dispatch) =>
    dispatch(
      asyncRequest({
        type: UPDATE_ORGANIZATION_CONFIGURATION,
        method: `PUT`,
        url,
        payload: updateRequest,
        meta: { organizationId },
        crumbs: [organizationId],
      }),
    )
      .then(() => invalidateGetUserMembershipsQuery())
      .then(() => invalidateGetOrganizationQuery())
}

export const resetFetchOrganizationsRequest = (...crumbs: string[]) =>
  resetAsyncRequest(FETCH_ORGANIZATIONS, crumbs)

export const resetFetchOrganizationRequest = (...crumbs: string[]) =>
  resetAsyncRequest(FETCH_ORGANIZATION, crumbs)

export const resetAcceptOrganizationInvitationRequest = (...crumbs: string[]) =>
  resetAsyncRequest(ACCEPT_ORGANIZATION_INVITATION, crumbs)

export const resetUpsertOrganizationInvitationsRequest = (...crumbs: string[]) =>
  resetAsyncRequest(UPSERT_ORGANIZATION_INVITATION, crumbs)

export const resetUpdateOrganizationRequest = (...crumbs: string[]) =>
  resetAsyncRequest(UPDATE_ORGANIZATION, crumbs)

export const resetUpdateRoleAssignmentsRequest = (...crumbs: string[]) =>
  resetAsyncRequest(UPDATE_ORGANIZATION_MEMBERSHIP_ROLE_ASSIGNMENTS, crumbs)

export const resetAddOrganizationMemberRequest = (...crumbs: string[]) =>
  resetAsyncRequest(ADD_ORGANIZATION_MEMBER, crumbs)
